// saleInvoice/UpdateSaleInvoice.jsx
import React, { useEffect, useState } from "react";
import Select from "react-select";
import validator from "validator";
import {
  Button,
  Card,
  Form,
  Container,
  Row,
  Col,
  Table,
  Badge,
} from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import {
  updateSaleInvoice,
  fetchSaleInvoices,
} from "../../../../../Redux/saleInvoiceSlice";
import { useInvoiceData, notify } from "../hooks/useInvoiceData";

function UpdateSaleInvoice() {
  const {
    date,
    setDate,
    invoiceItems,
    setInvoiceItems,
    invoiceNumber,
    setInvoiceNumber,
    products,
    clients,
    client,
    setClient,
    totalHT,
    totalTTC,
    totalVAT,
    status,
    setStatus,
    deliveryNotes,
    setDeliveryNotes,
    loadingDeliveryNotes,
    setLoadingDeliveryNotes,
    invoice, // Add this from useInvoiceData

    id,
    dispatch,
    navigate,
    calculateTotals,
  } = useInvoiceData("SALE_INVOICE", true);

  // NEW: State for selected delivery notes
  const [selectedDeliveryNotes, setSelectedDeliveryNotes] = useState([]);
  const [loadingConsolidation, setLoadingConsolidation] = useState(false);
  const [existingConsolidations, setExistingConsolidations] = useState([]);

  // Fetch delivery notes for clients
  useEffect(() => {
    const fetchDeliveryNotes = async () => {
      if (client?.value) {
        setLoadingDeliveryNotes(true);
        try {
          const response = await dispatch(
            fetchSaleInvoices({
              type: "DELIVERY_NOTE",
              status: "VALIDATED",
            }),
          );
          const filteredInvoices = (response.payload || []).filter(
            (inv) => inv.client.id === client.value,
          );
          setDeliveryNotes(filteredInvoices);
        } catch (error) {
          notify(2, "Erreur lors du chargement des bons de livraison");
          setDeliveryNotes([]);
        } finally {
          setLoadingDeliveryNotes(false);
        }
      }
    };

    fetchDeliveryNotes();
  }, [client, dispatch]);

  // NEW: Load existing consolidations when invoice data is available
  useEffect(() => {
    if (invoice?.consolidatedDeliveryNotes) {
      const consolidations = invoice.consolidatedDeliveryNotes.map(
        (cons) => cons.sourceDeliveryNote,
      );
      setExistingConsolidations(consolidations);

      // Set selected delivery notes from existing consolidations
      if (consolidations.length > 0) {
        setSelectedDeliveryNotes(
          consolidations.map((note) => ({
            value: note.id,
            label: `${note.invoiceNumber} - ${new Date(note.date).toLocaleDateString()} - ${note.totalTTC?.toFixed(2)} TND`,
            note: note,
          })),
        );
      }
    }
  }, [invoice]);

  useEffect(() => {
    calculateTotals();
  }, [invoiceItems, calculateTotals]);

  const handleItemChange = (index, field, value) => {
    const newItems = invoiceItems.map((item, i) => {
      if (i === index) {
        let updatedItem = { ...item, [field]: value };

        if (field === "productId") {
          const selectedProduct = products.find((p) => p.id === value);
          if (selectedProduct) {
            updatedItem.price =
              selectedProduct.salePrice || selectedProduct.price || 0;
            updatedItem.vatRate = selectedProduct.vat || 0; // FIXED: Changed from vatRate to vat
          }
        }

        const itemHT = (updatedItem.price || 0) * (updatedItem.quantity || 0);
        const itemVAT = itemHT * ((updatedItem.vatRate || 0) / 100);

        updatedItem.totalHT = itemHT;
        updatedItem.vatAmount = itemVAT;
        updatedItem.totalTTC = itemHT + itemVAT;

        return updatedItem;
      }
      return item;
    });

    setInvoiceItems(newItems);
  };

  const handleAddItem = () => {
    setInvoiceItems([
      ...invoiceItems,
      {
        productId: "",
        quantity: 1,
        price: 0,
        vatRate: 0,
        vatAmount: 0,
        totalHT: 0,
        totalTTC: 0,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(newItems);
  };

  // NEW: Handle selection of multiple delivery notes
  const handleDeliveryNoteSelect = (selectedOptions) => {
    setSelectedDeliveryNotes(selectedOptions || []);
  };

  // NEW: Load data from selected delivery notes
  const loadSelectedDeliveryNotes = async () => {
    if (selectedDeliveryNotes.length === 0) {
      notify(2, "Veuillez sélectionner au moins un bon de livraison");
      return;
    }

    if (
      !window.confirm(
        `Voulez-vous remplacer les données avec ${selectedDeliveryNotes.length} bon(s) de livraison ?`,
      )
    ) {
      return;
    }

    setLoadingConsolidation(true);

    try {
      // Get full delivery note data
      const selectedNotesData = deliveryNotes.filter((note) =>
        selectedDeliveryNotes.some((selected) => selected.value === note.id),
      );

      // Combine all items from selected delivery notes
      const combinedItems = [];
      selectedNotesData.forEach((note) => {
        note.items.forEach((item) => {
          combinedItems.push({
            productId: item.productId,
            quantity: item.quantity,
            price: item.price,
            vatRate: item.vatRate,
            vatAmount: item.vatAmount,
            totalHT: (item.price || 0) * (item.quantity || 0),
            totalTTC:
              (item.price || 0) *
              (item.quantity || 0) *
              (1 + (item.vatRate || 0) / 100),
          });
        });
      });

      // Set invoice items with combined data
      setInvoiceItems(combinedItems);

      notify(1, "Données chargées avec succès");
    } catch (error) {
      notify(2, "Erreur lors du chargement des données");
    } finally {
      setLoadingConsolidation(false);
    }
  };

  // NEW: Clear selected delivery notes
  const handleClearSelection = () => {
    setSelectedDeliveryNotes([]);
  };

  const loadDeliveryNoteData = (deliveryNote) => {
    if (
      window.confirm("Voulez-vous charger les données de ce bon de livraison ?")
    ) {
      setInvoiceNumber(deliveryNote.invoiceNumber);
      setDate(deliveryNote.date.split("T")[0]);

      setClient({
        value: deliveryNote.client.id,
        label: deliveryNote.client.name,
        client: deliveryNote.client,
      });

      setInvoiceItems(
        deliveryNote.items.map((item) => ({
          id: item.id,
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
          vatRate: item.vatRate,
          vatAmount: item.vatAmount,
          totalHT: (item.price || 0) * (item.quantity || 0),
          totalTTC:
            (item.price || 0) *
            (item.quantity || 0) *
            (1 + (item.vatRate || 0) / 100),
        })),
      );
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: { bg: "secondary", text: "Brouillon" },
      VALIDATED: { bg: "success", text: "Validée" },
      PAID: { bg: "primary", text: "Payée" },
      CANCELLED: { bg: "danger", text: "Annulée" },
    };
    const config = statusConfig[status] || { bg: "light", text: status };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const submitForm = async () => {
    if (validator.isEmpty(invoiceNumber)) {
      notify(2, "Numéro de facture est obligatoire");
      return;
    }

    if (!client) {
      notify(2, "Client est obligatoire");
      return;
    }

    if (invoiceItems.length === 0) {
      notify(2, "Au moins un article est obligatoire");
      return;
    }

    for (let i = 0; i < invoiceItems.length; i++) {
      if (!invoiceItems[i].productId) {
        notify(2, `L'article ${i + 1} doit avoir un produit sélectionné`);
        return;
      }
    }

    const items = invoiceItems.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
      price: item.price,
      vatRate: item.vatRate,
      vatAmount: item.vatAmount,
    }));

    try {
      const invoiceData = {
        invoiceNumber,
        date,
        type: "SALE_INVOICE",
        status,
        items,
        totalHT,
        totalTTC,
        clientId: client.value,
        // NEW: Add selected delivery note IDs
        deliveryNoteIds: selectedDeliveryNotes.map((note) => note.value),
      };

      await dispatch(updateSaleInvoice({ id, ...invoiceData }));
      notify(1, "Facture de vente mise à jour avec succès");

      setTimeout(() => navigate.push("/sale-invoices/list/SALE_INVOICE"), 1500);
    } catch (error) {
      notify(2, "Erreur lors de la mise à jour");
    }
  };

  const statusOptions = [
    { value: "DRAFT", label: "Brouillon" },
    { value: "VALIDATED", label: "Validée" },
    { value: "PAID", label: "Payée" },
    { value: "CANCELLED", label: "Annulée" },
  ];

  // NEW: Prepare delivery note options for multi-select
  const deliveryNoteOptions = deliveryNotes.map((note) => ({
    value: note.id,
    label: `${note.invoiceNumber} - ${new Date(note.date).toLocaleDateString()} - ${note.totalTTC?.toFixed(2)} TND`,
    note: note,
  }));

  const getSelectedProductOption = (productId) => {
    if (!productId) return null;
    const product = products.find((p) => p.id === productId);
    return product
      ? {
          value: product.id,
          label: product.name,
          price: product.salePrice || product.price || 0,
          vatRate: product.vat,
        }
      : null;
  };

  return (
    <Container fluid>
      <ToastContainer />
      <Row>
        <Col md="12">
          <Button
            className="btn-wd btn-outline mr-1 float-left"
            variant="info"
            onClick={() => navigate.push("/sale-invoices/list/SALE_INVOICE")}
          >
            <span className="btn-label">
              <i className="fas fa-list"></i>
            </span>
            Retour à la liste
          </Button>
        </Col>
      </Row>

      <Form className="form">
        <Card>
          <Card.Header>
            <Card.Title as="h4">Modifier facture de vente</Card.Title>
          </Card.Header>

          <Card.Body>
            <Row>
              <Col md="4">
                <Form.Group>
                  <label>Numéro de facture* </label>
                  <Form.Control
                    value={invoiceNumber}
                    placeholder="Ex: FAC-2023-001"
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group>
                  <label>Type de facture* </label>
                  <Form.Control value="Facture vente" readOnly />
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group>
                  <label>Statut </label>
                  <Select
                    value={statusOptions.find((opt) => opt.value === status)}
                    options={statusOptions}
                    onChange={(e) => setStatus(e.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md="4">
                <Form.Group>
                  <label>Date* </label>
                  <Form.Control
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md="4">
                <Form.Group>
                  <label>Client* </label>
                  <Select
                    placeholder="Sélectionner un client"
                    value={client}
                    options={clients.map((client) => ({
                      label: client.name,
                      value: client.id,
                      client: client,
                    }))}
                    onChange={(e) => {
                      setClient(e);
                      setSelectedDeliveryNotes([]); // Clear selection when client changes
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* NEW: Multi-select Delivery Notes Section */}
            {client?.value && (
              <>
                <Row className="mt-3">
                  <Col md="12">
                    <Card className="bg-light border-primary">
                      <Card.Header className="bg-primary text-white">
                        <Card.Title as="h6" className="mb-0">
                          <i className="fas fa-layer-group mr-2"></i>
                          Consolidation de bons de livraison
                          {loadingDeliveryNotes && (
                            <span className="ml-2">
                              <i className="fas fa-spinner fa-spin"></i>
                            </span>
                          )}
                        </Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md="8">
                            <Form.Group>
                              <label>
                                Sélectionner les bons de livraison à consolider
                              </label>
                              <Select
                                isMulti
                                placeholder="Choisir un ou plusieurs bons de livraison..."
                                value={selectedDeliveryNotes}
                                options={deliveryNoteOptions}
                                onChange={handleDeliveryNoteSelect}
                                isDisabled={loadingDeliveryNotes}
                                isLoading={loadingDeliveryNotes}
                                closeMenuOnSelect={false}
                              />
                              {selectedDeliveryNotes.length > 0 && (
                                <small className="text-muted">
                                  {selectedDeliveryNotes.length} bon(s)
                                  sélectionné(s)
                                </small>
                              )}
                              {existingConsolidations.length > 0 &&
                                selectedDeliveryNotes.length === 0 && (
                                  <small className="text-muted d-block mt-1">
                                    <i className="fas fa-info-circle mr-1"></i>
                                    {existingConsolidations.length} bon(s)
                                    actuellement consolidé(s)
                                  </small>
                                )}
                            </Form.Group>
                          </Col>
                          <Col md="4" className="d-flex align-items-end">
                            <Button
                              variant="primary"
                              className="mr-2"
                              onClick={loadSelectedDeliveryNotes}
                              disabled={
                                selectedDeliveryNotes.length === 0 ||
                                loadingConsolidation
                              }
                            >
                              {loadingConsolidation ? (
                                <>
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Chargement...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-sync mr-2"></i>
                                  Mettre à jour les articles
                                </>
                              )}
                            </Button>
                            {selectedDeliveryNotes.length > 0 && (
                              <Button
                                variant="secondary"
                                onClick={handleClearSelection}
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Existing Delivery Notes List (for reference) */}
                <Row className="mt-3">
                  <Col md="12">
                    <Card className="bg-light">
                      <Card.Header>
                        <Card.Title as="h6">
                          <i className="fas fa-file-invoice mr-2"></i>
                          Bons de livraison disponibles
                          <Badge bg="info" className="ml-2">
                            {deliveryNotes.length}
                          </Badge>
                        </Card.Title>
                      </Card.Header>
                      <Card.Body
                        style={{ maxHeight: "250px", overflowY: "auto" }}
                      >
                        {deliveryNotes.length > 0 ? (
                          <Table size="sm" striped bordered hover>
                            <thead>
                              <tr>
                                <th>N° Bon</th>
                                <th>Date</th>
                                <th>Total TTC</th>
                                <th>Statut</th>
                                <th>Articles</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deliveryNotes.map((note) => {
                                const isSelected = selectedDeliveryNotes.some(
                                  (s) => s.value === note.id,
                                );
                                const isExisting = existingConsolidations.some(
                                  (e) => e.id === note.id,
                                );
                                return (
                                  <tr
                                    key={note.id}
                                    className={
                                      isSelected
                                        ? "table-primary"
                                        : isExisting
                                          ? "table-success"
                                          : ""
                                    }
                                  >
                                    <td>
                                      {note.invoiceNumber}
                                      {isExisting && (
                                        <Badge bg="success" className="ml-2">
                                          Actuel
                                        </Badge>
                                      )}
                                    </td>
                                    <td>
                                      {new Date(note.date).toLocaleDateString()}
                                    </td>
                                    <td className="text-right">
                                      {note.totalTTC?.toFixed(2)} TND
                                    </td>
                                    <td>{getStatusBadge(note.status)}</td>
                                    <td className="text-center">
                                      <Badge bg="secondary">
                                        {note.items?.length || 0}
                                      </Badge>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        ) : (
                          <p className="text-muted text-center mb-0">
                            Aucun bon de livraison trouvé
                          </p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            {/* Items Table */}
            <Row>
              <Col md="12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Button variant="success" onClick={handleAddItem}>
                    <i className="fas fa-plus mr-2"></i>
                    Ajouter un article
                  </Button>
                  {(selectedDeliveryNotes.length > 0 ||
                    existingConsolidations.length > 0) && (
                    <Badge bg="primary" className="p-2">
                      <i className="fas fa-layer-group mr-1"></i>
                      {selectedDeliveryNotes.length > 0
                        ? `${selectedDeliveryNotes.length} bon(s) sélectionné(s)`
                        : `${existingConsolidations.length} bon(s) consolidé(s)`}
                    </Badge>
                  )}
                </div>

                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Quantité</th>
                      <th>Prix unitaire (TND)</th>
                      <th>TVA %</th>
                      <th>Montant TVA</th>
                      <th>Total HT</th>
                      <th>Total TTC</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceItems.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Select
                            placeholder="Sélectionner un produit"
                            value={getSelectedProductOption(item.productId)}
                            options={products.map((product) => ({
                              label: product.name,
                              value: product.id,
                              price: product.salePrice || product.price || 0,
                              vatRate: product.vat,
                            }))}
                            onChange={(selectedOption) =>
                              handleItemChange(
                                index,
                                "productId",
                                selectedOption.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <Form.Control
                            value={item.quantity || 0}
                            type="number"
                            min="1"
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantity",
                                parseInt(e.target.value) || 0,
                              )
                            }
                          />
                        </td>
                        <td>
                          <Form.Control
                            value={item.price || 0}
                            type="number"
                            step="0.01"
                            min="0"
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "price",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                          />
                        </td>
                        <td>
                          <Form.Control
                            value={item.vatRate}
                            type="number"
                            step="0.01"
                            min="0"
                            readOnly
                          />
                        </td>
                        <td className="text-right">
                          {(item.vatAmount || 0).toFixed(2)}
                        </td>
                        <td className="text-right">
                          {(item.totalHT || 0).toFixed(2)}
                        </td>
                        <td className="text-right">
                          {(item.totalTTC || 0).toFixed(2)}
                        </td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Totals */}
            <Row className="mt-3">
              <Col md="3" className="offset-md-6">
                <Form.Group>
                  <label>Total HT</label>
                  <Form.Control
                    value={(totalHT || 0).toFixed(2)}
                    readOnly
                    className="text-right font-weight-bold"
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <label>Total TVA</label>
                  <Form.Control
                    value={(totalVAT || 0).toFixed(2)}
                    readOnly
                    className="text-right"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md="3" className="offset-md-9">
                <Form.Group>
                  <label>Total TTC</label>
                  <Form.Control
                    value={(totalTTC || 0).toFixed(2)}
                    readOnly
                    className="text-right font-weight-bold text-primary"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>

          <Card.Footer>
            <Button
              className="btn-wd btn-outline mr-1 float-left"
              variant="primary"
              onClick={submitForm}
            >
              <i className="fas fa-sync mr-2"></i>
              Mettre à jour
            </Button>
          </Card.Footer>
        </Card>
      </Form>
    </Container>
  );
}

export default UpdateSaleInvoice;
